/* eslint-disable max-len */
/**
 * Handles the Listrak "Notify me when back in stock" form inside the
 * notify-me modal (see snippets/notify-me-modal.liquid).
 *
 * Dependencies:
 * - Listrak `_ltk.Alerts` global, loaded by the Listrak tracking app embed.
 * - `modal-dialog` / `modal-opener` custom elements (assets/main.js).
 */

if (!customElements.get('notify-me-form')) {
  class NotifyMeForm extends HTMLElement {
    constructor() {
      super();
      this.skuInput = this.querySelector('.js-notify-sku');
      this.alertCodeInput = this.querySelector('.js-notify-alert-code');
      this.emailInput = this.querySelector('.js-notify-email');
      this.phoneInput = this.querySelector('.js-notify-phone');
      this.errorEl = this.querySelector('.js-form-error');
      this.fieldsWrapEl = this.querySelector('.notify-me-form__fields-wrap');
      this.successEl = this.querySelector('.notify-me-form__success');
      this.productNameEls = this.querySelectorAll('.js-notify-product-name');
      this.submitBtn = this.querySelector('.js-notify-submit');

      this.boundHandleSubmit = this.handleSubmit.bind(this);
      this.submitBtn?.addEventListener('click', this.boundHandleSubmit);
    }

    /**
     * Populates the form for the variant whose "Notify me" button was clicked,
     * and resets any previous error/success state.
     * @param {Element} opener - The button that opened the parent modal.
     */
    setVariant(opener) {
      this.skuInput.value = opener?.dataset.notifySku || '';
      const productName = opener?.dataset.notifyProductTitle || '';
      this.productNameEls.forEach((el) => { el.textContent = productName; });

      this.emailInput.value = '';
      this.phoneInput.value = '';
      this.errorEl.hidden = true;
      this.fieldsWrapEl.hidden = false;
      this.successEl.hidden = true;
    }

    /**
     * Handles 'click' events on the submit button.
     */
    handleSubmit() {
      if (!this.emailInput.checkValidity()) {
        this.showError(this.emailInput.validationMessage);
        this.emailInput.focus();
        return;
      }

      // Phone is optional, but if one was entered it should look like a phone number.
      const phone = this.phoneInput.value.trim();
      let phoneDigits = phone.replace(/\D/g, '');
      if (phone) {
        // Assume a bare 10-digit number is a US number missing its country code.
        if (phoneDigits.length === 10) {
          phoneDigits = `1${phoneDigits}`;
        }
        if (phoneDigits.length !== 11 || !phoneDigits.startsWith('1')) {
          this.showError(this.dataset.phoneErrorMessage);
          this.phoneInput.focus();
          return;
        }
      }

      if (typeof _ltk === 'undefined' || !_ltk.Alerts) {
        this.showError(this.dataset.errorMessage);
        return;
      }

      const identifiers = { Email: this.emailInput.value };
      if (phoneDigits) {
        identifiers.PhoneNumber = phoneDigits;
      }

      _ltk.Alerts.AddAlertWithIdentifiers({
        Identifiers: identifiers,
        Sku: this.skuInput.value,
        AlertCode: this.alertCodeInput.value
      });
      _ltk.Alerts.Submit();
      console.log("send alert subscription for " + this.skuInput.value + " to listrak");

      this.errorEl.hidden = true;
      this.fieldsWrapEl.hidden = true;
      this.successEl.hidden = false;
    }

    /**
     * Displays an error message.
     * @param {string} message - Error message to display.
     */
    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  customElements.define('notify-me-form', NotifyMeForm);
}

// Sync the modal's form with whichever "Notify me" button opened it.
// (modal-opener has already opened the modal by the time this runs, since
// this listens on the bubble phase after the button's own click handler.)
document.addEventListener('click', (evt) => {
  const opener = evt.target.closest('.notify-me-btn');
  if (!opener) return;

  const modalId = opener.closest('modal-opener')?.dataset.modal;
  const modal = modalId && document.getElementById(modalId);
  const form = modal?.querySelector('notify-me-form');
  form?.setVariant(opener);
});
